import {
  resolveModbusStatus,
  type ModbusCommunicationStatus,
} from "@/features/devices/modbusDiagnostic";
import type { LatestPort } from "@/features/devices/types";

/**
 * Resumo de saude do barramento Modbus de UM device.
 *
 * Existe para nao precisar de um card de diagnostico por registrador: com 16
 * pontos, materializar 16 diagnosticos afogaria o painel operacional e o
 * operador ainda teria que somar de cabeca quantos estao mudos.
 *
 * Diferente dos demais widgets, este e de DEVICE e nao de sinal — o que ele
 * responde ("o barramento esta saudavel?") nao pertence a nenhum ponto
 * individual. A ancora serve so para identificar o slot.
 */

export interface ModbusHealthSummary {
  total: number;
  ok: number;
  /**
   * Pontos em falha, agrupados por tipo, na ordem de gravidade. `count` conta
   * TODOS os pontos naquele estado; `ports` lista apenas os de indice
   * conhecido, e por isso pode ser mais curta que `count`.
   */
  failures: Array<{
    status: Exclude<ModbusCommunicationStatus, "ok">;
    count: number;
    ports: number[];
  }>;
  /** Nenhum ponto respondeu ainda desde o boot da placa. */
  allNever: boolean;
}

/**
 * Ordem de GRAVIDADE, nao a ordem numerica do contrato. `never` vem por
 * ultimo: no boot todos os pontos passam por ele, e listar "nunca leu" acima
 * de uma excecao real inverteria a atencao do operador.
 */
const FAILURE_ORDER: Array<Exclude<ModbusCommunicationStatus, "ok">> = [
  "exception",
  "bad-frame",
  "timeout",
  "never",
];

export function summarizeModbusHealth(ports: LatestPort[]): ModbusHealthSummary {
  // CONTAGEM e LISTA sao coisas separadas de proposito. `portIndex` vem de JSON
  // de rede, onde o tipo declarado nao vale: um ponto sem indice legivel ainda
  // e um ponto do barramento e precisa entrar na conta. Contar so quem tem
  // indice fazia `ok + falhas` nao fechar com `total` ("7 de 8 ok" sem nenhuma
  // falha listada) e derrubava o `allNever`, transformando um boot normal em
  // alarme.
  const countByStatus = new Map<ModbusCommunicationStatus, number>();
  const portsByStatus = new Map<ModbusCommunicationStatus, number[]>();
  let total = 0;

  for (const port of ports) {
    const status = resolveModbusStatus(port.mode, port.s);
    // `resolveModbusStatus` devolve `null` fora do modo MB e para `s`
    // ilegivel: um ponto sem status conhecido nao entra na conta, em vez de
    // ser contado como saudavel.
    if (!status) continue;
    total += 1;
    countByStatus.set(status, (countByStatus.get(status) ?? 0) + 1);
    if (typeof port.portIndex === "number") {
      portsByStatus.set(status, [...(portsByStatus.get(status) ?? []), port.portIndex]);
    }
  }

  const failures = FAILURE_ORDER.flatMap((status) => {
    const count = countByStatus.get(status) ?? 0;
    if (count === 0) return [];
    const known = portsByStatus.get(status) ?? [];
    return [{ status, count, ports: [...known].sort((a, b) => a - b) }];
  });

  const ok = countByStatus.get("ok") ?? 0;
  return {
    total,
    ok,
    failures,
    allNever: total > 0 && (countByStatus.get("never") ?? 0) === total,
  };
}
